import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { 
  Book, 
  Layers, 
  FileText, 
  Zap, 
  Code, 
  Settings, 
  ChevronDown, 
  ChevronRight 
} from "lucide-react"
import { docsNavigation } from "@/data/helpData"
import type { NavigationItem } from "@/data/helpData"

interface HelpSidebarProps {
  currentPath: string
}

interface NavItemComponentProps {
  item: NavigationItem
}

function NavItemComponent({ item }: NavItemComponentProps) {
  const isChildActive = item.children?.some((child) => child.isActive) ?? false
  const initialExpanded = item.isActive || isChildActive
  const [expanded, setExpanded] = useState(initialExpanded)

  useEffect(() => {
    const shouldBeExpanded = item.isActive || isChildActive
    if (shouldBeExpanded !== expanded) {
      setExpanded(shouldBeExpanded)
    }
  }, [item.isActive, isChildActive, expanded])

  const baseClasses = 'flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150'
  const activeClasses = 'bg-purple-100 dark:bg-purple-900/40 text-purple-900 dark:text-purple-200'
  const inactiveClasses = 'text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-900 dark:hover:text-purple-300'

  // Icon mapping
  const getIcon = (iconName: string) => {
    const iconProps = { className: "h-4 w-4 mr-2.5 text-purple-700 dark:text-purple-400" }
    switch (iconName) {
      case 'book': return <Book {...iconProps} />
      case 'layers': return <Layers {...iconProps} />
      case 'fileText': return <FileText {...iconProps} />
      case 'zap': return <Zap {...iconProps} />
      case 'code': return <Code {...iconProps} />
      case 'settings': return <Settings {...iconProps} />
      default: return <FileText {...iconProps} />
    }
  }

  if (item.children) {
    const parentActiveStyle = item.isActive ? activeClasses : inactiveClasses

    return (
      <div>
        <button 
          onClick={() => setExpanded(!expanded)} 
          className={`${baseClasses} justify-between ${parentActiveStyle}`}
        >
          <div className="flex items-center">
            {getIcon('layers')}
            <span>{item.title}</span>
          </div>
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        {expanded && (
          <div className="ml-4 mt-1 space-y-1 border-l-2 border-purple-200 dark:border-purple-700 pl-3">
            {item.children.map((child, childIndex) => (
              <Link
                key={childIndex}
                to={child.path}
                className={`${baseClasses} ${child.isActive ? activeClasses : inactiveClasses}`}
              >
                {getIcon('fileText')}
                <span>{child.title}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <Link 
      to={item.path} 
      className={`${baseClasses} ${item.isActive ? activeClasses : inactiveClasses}`}
    >
      {getIcon(item.id)}
      <span>{item.title}</span>
    </Link>
  )
}

export default function HelpSidebar({ currentPath }: HelpSidebarProps) {
  // Update navigation items with current active state
  const navigationWithActiveState = docsNavigation.map(item => ({
    ...item,
    isActive: currentPath === item.path,
    children: item.children?.map(child => ({
      ...child,
      isActive: currentPath === child.path
    }))
  }))

  return (
    <div className="w-full">
      <div className="space-y-1">
        {navigationWithActiveState.map((item, index) => (
          <NavItemComponent key={index} item={item} />
        ))}
      </div>
    </div>
  )
}